import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';

import { useTheme } from '../../../contexts/ThemeContext';
import QuestionStatus from '../../partials/QuestionStatus';

import styles from '../../../../styles/items/question-result-default.module.scss';

export interface QuestionResultProps {
    index: number;
    question: Question;
    answer: ParticipationAnswer | null;
    withQuestionBody?: boolean;
    withLongAnswer?: boolean;
    withValidAnswer?: boolean;
    withExplanation?: boolean;
    withNumber?: boolean;
    className?: string;
}

function DefaultQuestionResult({
    index,
    question,
    answer,
    withQuestionBody = false,
    withLongAnswer = true,
    withValidAnswer = true,
    withExplanation = false,
    withNumber = true,
    className,
}: QuestionResultProps) {
    const { name: theme } = useTheme();
    const { id, validAnswer = null, explanation = null, body = null, choices = null } = question;
    const validChoice = (choices || []).find(({ id: choiceId }) => choiceId === validAnswer);
    const { label: validChoiceLabel = null } = validChoice || {};
    const { valid } = answer || {};
    const number = (index + 1).toString().padStart(2, '0');
    const finalValidAnswer = validChoiceLabel || validAnswer;
    const defaultValidLabel = withLongAnswer ? (
        <FormattedMessage
            defaultMessage="That's right! The right answer is {correct}."
            description="Game question results label"
            values={{
                correct: finalValidAnswer,
            }}
        />
    ) : (
        validChoiceLabel
    );

    const defaultInvalidLabel = withLongAnswer ? (
        <FormattedMessage
            defaultMessage="Wrong, the right answer is {correct}."
            description="Game question results label"
            values={{
                correct: finalValidAnswer,
            }}
        />
    ) : (
        validChoiceLabel
    );

    const defaultLabel = valid ? defaultValidLabel : defaultInvalidLabel;

    const label = withValidAnswer && finalValidAnswer !== null ? finalValidAnswer : defaultLabel;

    return (
        <div
            key={`question-${id}`}
            className={classNames([
                styles.container,
                {
                    [styles.valid]: valid === true,
                    [styles.invalid]: valid === false,
                },
                className,
            ])}
        >
            {withNumber ? (
                <span className={styles.number}>
                    {number}
                    {theme === 'nhl' ? (
                        <QuestionStatus answer={answer} className={styles.status} />
                    ) : null}
                </span>
            ) : null}
            <div className={styles.inner}>
                {withQuestionBody && body !== null ? (
                    <span className={styles.body}>{body}</span>
                ) : null}
                <span className={styles.label}>{label}</span>
                {withExplanation && explanation !== null ? (
                    <span className={styles.explanation}>{explanation}</span>
                ) : null}
            </div>
        </div>
    );
}

export default DefaultQuestionResult;
